import { CartItem } from 'models/CartItem';
import styles from './cartItem.scss';
import connect from 'components/Shared/connect';
import Image from 'components/Shared/Image';
import { Price } from 'components/Shared/Price/themes/BGH/price';
import { ProductMeta, StockStatus } from 'models/ProductMeta';
import cn from 'classnames';
import { updateQuantity, cartProductRemove } from 'components/Cart/actions';
import { Button, LoaderButton } from 'components/Button';
import { useState } from 'react';
import { LoadingStatus } from 'models/LoadingStatus';
import { isMultiQuantity } from 'components/Basket/selector';
import { hasElements, NO_ELEMENT } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { SaleStatus } from 'models/SaleStatus';
import {
  getIsRentalProduct,
  getPriceWithRent,
  getRentalUnit
} from 'components/Shared/Price/selector';
import { Relation } from 'models/Relation';
import { RelationshipType } from 'models/RelationshipType';
import { useSelector } from 'react-redux';
import { selectShouldUseNewCart } from 'views/CheckoutPage/DeliveryInformation/selector';
import { getBobSubItemRelations } from 'components/Cart/selector';
import { GTM_LIST_NAME } from 'store/googleTagManager/types';

export const ProductStockStatus = ({
  stockStatus,
  stock,
  saleStatus
}: Pick<ProductMeta, 'stockStatus' | 'stock' | 'saleStatus'>) => {
  if ([StockStatus.Order, StockStatus.Storage].includes(stockStatus)) {
    return NO_ELEMENT;
  }

  return (
    <div
      className={cn(styles.stockStatus, {
        [styles.vague]: stockStatus === StockStatus.Create || saleStatus === SaleStatus.NotInStock,
        [styles.doubtful]: stockStatus === StockStatus.End
      })}
    >
      {stock}
      {stockStatus === StockStatus.Create && ' *'}
    </div>
  );
};

type QuantityProps = Pick<MergedProps, 'quantityChange'> & {
  cartItem: CartItem;
  disabled: boolean;
};
const Quantity = ({ quantityChange, disabled, cartItem }: QuantityProps) => {
  const { amount, lowest, highest, salesQuantity, unit } = cartItem.quantity;
  const { rentalPrice, unitPrice } = cartItem;

  const shouldUseNewCart = useSelector(selectShouldUseNewCart);

  const amountToDisplay = shouldUseNewCart ? salesQuantity : amount;

  const [currentAmount, setAmount] = useState(amount);
  const isRentalProduct = getIsRentalProduct({ rentalPrice });

  const shouldRenderQuantity =
    isMultiQuantity(cartItem.quantity) && !isRentalProduct && !shouldUseNewCart;

  const decrease = () => {
    const newAmount = currentAmount - 1;
    if (newAmount < lowest) {
      return;
    }

    setAmount(newAmount);
    quantityChange(newAmount);
  };
  const increase = () => {
    const newAmount = currentAmount + 1;
    if (newAmount > highest) {
      return;
    }

    setAmount(newAmount);
    quantityChange(newAmount);
  };

  return (
    <div className={styles.cartItemQuantity}>
      <div className={cn(styles.quantityButtons, { [styles.disabled]: disabled })}>
        <Button
          disabled={disabled || currentAmount === lowest}
          onClick={decrease}
          className={styles.quantityButton}
          appearance="tertiary"
          {...getQaDataAttr('basket-quantity-decrease')}
        >
          -
        </Button>
        {disabled}
        <span>{amountToDisplay}</span>
        <Button
          disabled={disabled || currentAmount === highest}
          onClick={increase}
          className={styles.quantityButton}
          appearance="tertiary"
          {...getQaDataAttr('basket-quantity-increase')}
        >
          +
        </Button>
      </div>
      {shouldRenderQuantity && (
        <span className={styles.salesQuantity}>
          = {salesQuantity} {unit}
        </span>
      )}
      {currentAmount > 1 && !isRentalProduct && (
        <span className={styles.unitPrice}>
          <Price price={unitPrice} /> / <span>{unit}</span>
        </span>
      )}
    </div>
  );
};

interface MergedProps extends CartItem {
  quantityChange: (value: number) => Promise<void>;
  removeItem: () => Promise<void>;
  loading: boolean;
  relations?: Relation;
}

const IMAGE_DIMENSIONS = {
  width: 100,
  height: 100
};

const WRAPPER_STYLE = { width: '100%' };
const isProductUrl = (url: string) => url !== undefined && !url.startsWith('///');

const _CartItem = (props: MergedProps) => {
  const { originalPrice, linePrice } = props;
  const shouldUseNewCart = useSelector(selectShouldUseNewCart);
  const bobSubItemRelations = useSelector(getBobSubItemRelations);
  const bobRelation = bobSubItemRelations
    ?.filter(x => x.key === props.cartItemId)
    .map(item => item.value)
    .flatMap(relation => relation.items.map(item => ({ ...item, itemType: relation.type })));
  const cartItem = props as CartItem;
  const handleRemoveProduct = () => {
    return props.removeItem();
  };

  return (
    <li className={styles.base}>
      <div className={styles.cartItem}>
        {props.image.id ? (
          <a href={props.productUrl} className={styles.imageWrapper}>
            <Image
              className={styles.image}
              {...IMAGE_DIMENSIONS}
              {...props.image}
              paddingBottom
              wrapperStyle={WRAPPER_STYLE}
            />
          </a>
        ) : (
          <div className={cn(styles.imageWrapper, styles.image)} style={IMAGE_DIMENSIONS} />
        )}
        <div className={styles.cartItemInfo}>
          <div className={styles.row}>
            {isProductUrl(props.productUrl) ? (
              <a className={styles.productName} href={props.productUrl}>
                {props.displayName}
              </a>
            ) : (
              <span className={styles.productName}>{props.displayName}</span>
            )}
            <LoaderButton
              className={styles.removeButton}
              appearance="tertiary"
              onClick={() => handleRemoveProduct()}
              hideButtonWhileLoading
            >
              ×
            </LoaderButton>
          </div>
          {props.variantName && <p className={styles.variantName}>{props.variantName}</p>}
          <ProductStockStatus
            stockStatus={props.meta.stockStatus}
            stock={props.meta.stock}
            saleStatus={props.meta.saleStatus}
          />
          {originalPrice && originalPrice.amount > 0 && originalPrice.amount > linePrice.amount && (
            <Price
              appearance="lineThrough"
              className={styles.originalPrice}
              price={props.originalPrice.amount}
            />
          )}
          <div className={cn(styles.row, styles.linePriceRow)}>
            <Quantity
              disabled={props.loading}
              quantityChange={props.quantityChange}
              cartItem={cartItem}
            />
            <Price
              appearance="discount"
              className={styles.linePrice}
              unit={getRentalUnit(props)}
              price={getPriceWithRent(props)}
            />
          </div>
        </div>
      </div>
      <ul className={styles.relations}>
        {shouldUseNewCart
          ? bobRelation !== undefined &&
            bobRelation
              .filter(relation => relation.itemType !== RelationshipType.Package)
              .map(relation => (
                <li key={relation.cartItemId}>
                  <span className={styles.relationName}>
                    <span>
                      {relation.quantity.amount}&nbsp;{relation.quantity.unit}&nbsp;
                    </span>
                    {relation.displayName}
                  </span>
                  <Price
                    className={styles.linePrice}
                    appearance="discount"
                    unit={getRentalUnit(props)}
                    price={getPriceWithRent(relation)}
                  />
                </li>
              ))
          : hasElements(props.relations?.items) &&
            props.relations.type !== RelationshipType.Package &&
            props.relations.items.map(relation => (
              <li key={relation.cartItemId}>
                <span className={styles.relationName}>
                  <span>
                    {relation.quantity.amount}&nbsp;{relation.quantity.unit}&nbsp;
                  </span>
                  {relation.displayName}
                </span>
                <Price
                  className={styles.linePrice}
                  appearance="discount"
                  unit={getRentalUnit(props)}
                  price={getPriceWithRent(relation)}
                />
              </li>
            ))}
      </ul>
    </li>
  );
};

export const CartItemRow = connect<MergedProps, CartItem>(
  (state, { cartItemId }) => ({
    loading: state.cart.status === LoadingStatus.Fetching,
    relations: state.cart.subItemRelations[cartItemId]
  }),
  (dispatch, { cartItemId, productId }) => ({
    quantityChange: (quantity: number) =>
      dispatch(updateQuantity({ cartItemId, quantity, gtmListName: GTM_LIST_NAME.CART })),
    removeItem: () =>
      dispatch(cartProductRemove({ cartItemId, productId, gtmListName: GTM_LIST_NAME.CART }))
  })
)(_CartItem);
